import { formatDistanceToNow, format } from 'date-fns'
import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex, MarkdownPreview, Tooltip, Typography, UserAvatar } from '@percent/lemonade'
import { CommentBodyProps } from './CommentBody.types'
import styles from './CommentBody.module.scss'

function AdminBadge({ children }: PropsWithChildren) {
  return (
    <Flex align="center" className={styles.adminBadge} px={4} data-testid="comment-admin-badge">
      <Typography variant="small" $color="white">
        {children}
      </Typography>
    </Flex>
  )
}

export function CommentBody({ comment, isAdmin }: Readonly<CommentBodyProps>) {
  const { t } = useTranslation()

  const { author, content, createdAt } = comment
  const displayName = author.fullName ?? author.email ?? t('grants.candidateNotes.unknownAuthor')

  return (
    <>
      <Flex gap={8} align="center">
        <UserAvatar name={displayName} size={24} data-testid="comment-author-avatar" />
        <Flex gap={4}>
          <Typography variant="default-bold" className={styles.displayName}>
            {displayName}
          </Typography>
          {isAdmin && <AdminBadge>{t('typography.admin')}</AdminBadge>}
        </Flex>
        <Typography variant="small" className={styles.timestamp} data-testid="comment-timestamp">
          <Tooltip content={format(new Date(createdAt), 'MMMM d, yyyy HH:mm')}>
            {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
          </Tooltip>
        </Typography>
      </Flex>
      <Flex direction="column" gap={12} pl={32}>
        <MarkdownPreview content={content} fontSize="bodySmall" />
      </Flex>
    </>
  )
}
